import { nextDailyReset } from '../daily-limit/daily-limit';

/*
 * "Add to calendar" for tomorrow's practice, built in the browser: an .ics file
 * (Apple Calendar, Outlook) and a Google Calendar link. No service is called and
 * nothing about the student goes into either: a fixed title, the practice link
 * and the time.
 *
 * When: tomorrow (the student's next calendar day) at the time of day they are
 * practising now, rounded up to the next half hour, kept between 7:00 AM and
 * 9:00 PM. Never before the next 00:00 UTC, when the server's daily tally
 * restarts, so "your next questions are ready" is true when the event fires.
 */
export const REMINDER_EARLIEST_MINUTE = 7 * 60;
export const REMINDER_LATEST_MINUTE = 21 * 60;
export const REMINDER_DURATION_MINUTES = 15;
const HALF_HOUR_MS = 30 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

const deviceTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

/** Wall-clock parts of `date` in `timeZone` (the device's when undefined). */
export const zonedParts = (date, timeZone) => {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).forEach(({ type, value }) => { parts[type] = value; });
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
};

// The zone's offset from UTC at `date`, in ms (local minus UTC).
const offsetAt = (date, timeZone) => {
  const p = zonedParts(date, timeZone);
  const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return wallAsUtc - (date.getTime() - date.getUTCMilliseconds());
};

/** The instant a wall-clock time in `timeZone` names. Two passes settle DST. */
export const zonedTimeToUtc = ({ year, month, day, hour, minute }, timeZone) => {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let guess = wall;
  for (let pass = 0; pass < 3; pass += 1) {
    const next = wall - offsetAt(new Date(guess), timeZone);
    if (next === guess) break;
    guess = next;
  }
  return new Date(guess);
};

const roundUpToHalfHour = (date) => new Date(Math.ceil(date.getTime() / HALF_HOUR_MS) * HALF_HOUR_MS);

/** When tomorrow's practice reminder starts (see the header). */
export const reminderStart = (now = new Date(), { timeZone = deviceTimeZone() } = {}) => {
  const today = zonedParts(now, timeZone);
  let minutes = Math.ceil((today.hour * 60 + today.minute) / 30) * 30;
  minutes = Math.min(Math.max(minutes, REMINDER_EARLIEST_MINUTE), REMINDER_LATEST_MINUTE);
  const tomorrow = new Date(Date.UTC(today.year, today.month - 1, today.day + 1));
  const start = zonedTimeToUtc({
    year: tomorrow.getUTCFullYear(),
    month: tomorrow.getUTCMonth() + 1,
    day: tomorrow.getUTCDate(),
    hour: Math.floor(minutes / 60),
    minute: minutes % 60,
  }, timeZone);
  const reset = nextDailyReset(now);
  return start < reset ? roundUpToHalfHour(reset) : start;
};

const calendarDay = (date, timeZone) => {
  const p = zonedParts(date, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day);
};

/** "tomorrow at 3:00 PM" (or "today at …" / "Sunday at …"), in the student's clock. */
export const reminderPhrase = (start, now = new Date(), { timeZone = deviceTimeZone(), locale } = {}) => {
  // Newer ICU puts a narrow no-break space before AM/PM: any blank between the
  // parts becomes a plain one (the block keeps "3:00 PM" together itself).
  const time = new Intl.DateTimeFormat(locale, { timeZone, hour: 'numeric', minute: '2-digit' })
    .formatToParts(start)
    .map(({ type, value }) => (type === 'literal' && value.trim() === '' ? ' ' : value))
    .join('');
  const days = Math.round((calendarDay(start, timeZone) - calendarDay(now, timeZone)) / (24 * 60 * MINUTE_MS));
  if (days === 0) return `today at ${time}`;
  if (days === 1) return `tomorrow at ${time}`;
  const weekday = new Intl.DateTimeFormat(locale, { timeZone, weekday: 'long' }).format(start);
  return `${weekday} at ${time}`;
};

/** 20261101T200000Z */
export const icsDateTime = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

// RFC 5545 §3.3.11: backslash, semicolon, comma and newlines are escaped
// (a newline as the two characters backslash n).
const BACKSLASH = String.fromCodePoint(92);
export const escapeIcsText = (text) => String(text)
  .replace(/[\\;,]|\r?\n/g, (match) => BACKSLASH + (match.endsWith('\n') ? 'n' : match));

const utf8Length = (char) => {
  const code = char.codePointAt(0);
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  return code < 0x10000 ? 3 : 4;
};

// RFC 5545 §3.1: lines longer than 75 octets continue on the next line after a space.
export const foldIcsLine = (line) => {
  const out = [];
  let current = '';
  let octets = 0;
  for (const char of line) {
    const size = utf8Length(char);
    // The first line holds 75 octets; continuation lines hold 74 after the space.
    if (octets + size > (out.length ? 74 : 75)) {
      out.push(current);
      current = '';
      octets = 0;
    }
    current += char;
    octets += size;
  }
  out.push(current);
  return out.join('\r\n ');
};

/** Where the reminder sends the student: the ABSITE practice entry or Create Quiz. */
export const practiceLink = ({ origin, exam }) => {
  const base = exam === 'ABSITE' ? '/create-quiz?practice=absite&' : '/create-quiz?';
  return `${origin}${base}utm_source=calendar&utm_medium=reminder&utm_campaign=first_quiz_reminder`;
};

/**
 * The event's words: practice, never a count. "Your next 10 are ready" could be
 * false when it fires: a student who comes back after tonight's unlock spends
 * the allowance that also covers tomorrow afternoon (UX review, 2026-10-31).
 * The exam is named only when the plan covers it (the unlock line's rule).
 */
export const reminderEventText = ({ info, link }) => {
  const exam = info?.exam ? `${info.exam} ` : '';
  return { title: `QLearn: ${exam}practice`, description: `Time for your ${exam}practice. Start here: ${link}` };
};

// The event's UID only has to be unique: the time it was made, a per-page
// counter, and random bytes from the browser's crypto (every supported browser
// has it; without it the time and counter still differ).
let uidCount = 0;
const uniqueToken = () => {
  uidCount += 1;
  let random = '';
  try {
    const bytes = new Uint8Array(6);
    window.crypto.getRandomValues(bytes);
    random = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    random = '';
  }
  return `${Date.now().toString(36)}${uidCount.toString(36)}${random}`;
};

/** The .ics file, CRLF line endings, times in UTC, with an alarm at the start. */
export const buildIcs = ({
  start, title, description, link, now = new Date(), uid = `${icsDateTime(now)}-${uniqueToken()}`,
}) => {
  const end = new Date(start.getTime() + REMINDER_DURATION_MINUTES * MINUTE_MS);
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//QLearn//Practice reminder//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}@qlearn.medlearnity.com`,
    `DTSTAMP:${icsDateTime(now)}`,
    `DTSTART:${icsDateTime(start)}`,
    `DTEND:${icsDateTime(end)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    `URL:${link}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeIcsText(title)}`,
    'TRIGGER:PT0M',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].map(foldIcsLine).join('\r\n') + '\r\n';
};

/** A Google Calendar "new event" link with the same event filled in. */
export const googleCalendarUrl = ({ start, title, description }) => {
  const end = new Date(start.getTime() + REMINDER_DURATION_MINUTES * MINUTE_MS);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${icsDateTime(start)}/${icsDateTime(end)}`,
    details: description,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Saves the .ics through a temporary link. Returns false when the browser
 * can't (no Blob URLs), so the caller can say so instead of doing nothing.
 */
export const downloadIcs = (ics, filename = 'qlearn-practice.ics') => {
  try {
    if (typeof URL?.createObjectURL !== 'function' || typeof Blob !== 'function') return false;
    const href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => {
      try { URL.revokeObjectURL(href); } catch { /* nothing to free */ }
    }, 1000);
    return true;
  } catch {
    return false;
  }
};
